import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Calendar, Video, Bot, MessageSquare, ChevronRight, Star,
} from 'lucide-react';
import {
  getMyRequests,
  getAISessionHistory,
  getMyFeedback,
  extractRoomId,
} from '../../services/mockInterviewService';
import './DashboardWidgets.css';

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata',
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

const scoreColor = (s) => {
  const n = Number(s);
  if (Number.isNaN(n)) return '#94a3b8';
  if (n < 5) return '#ef4444';
  if (n < 7) return '#f59e0b';
  return '#10b981';
};

const DashboardWidgets = ({ dark = true }) => {
  const navigate = useNavigate();
  const [upcoming, setUpcoming] = useState([]);
  const [aiSessions, setAiSessions] = useState([]);
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    Promise.allSettled([getMyRequests(), getAISessionHistory(), getMyFeedback()])
      .then(([reqs, ai, fb]) => {
        if (!mounted) return;
        const reqList = Array.isArray(reqs.value) ? reqs.value : [];
        const now = Date.now();
        setUpcoming(
          reqList.filter((r) => {
            const status = (r.status || '').toLowerCase();
            const slot = r.requestedSlot || r.rescheduledSlot || r.scheduledAt || r.slotTime;
            // Show pending, confirmed and rescheduled — all are "scheduled" from user's view
            const isScheduled = ['pending', 'confirmed', 'rescheduled'].includes(status);
            return isScheduled && slot && new Date(slot).getTime() > now;
          }).sort((a, b) => {
            const aSlot = a.requestedSlot || a.rescheduledSlot || a.scheduledAt;
            const bSlot = b.requestedSlot || b.rescheduledSlot || b.scheduledAt;
            return new Date(aSlot) - new Date(bSlot);
          })
        );

        const aiList = Array.isArray(ai.value) ? ai.value : [];
        setAiSessions(
          aiList
            .filter((s) => (s.status || '').toLowerCase() === 'completed')
            .slice(0, 3)
        );

        const fbList = Array.isArray(fb.value) ? fb.value : [];
        setFeedbacks(fbList.slice(0, 3));
      })
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, []);

  if (loading) {
    return <div className="dw-loading">Loading widgets…</div>;
  }

  return (
    <div className={`dw-grid ${dark ? 'dw-dark' : ''}`}>
      <section className="dw-card">
        <div className="dw-head">
          <div className="dw-head-left">
            <div className="dw-ic" style={{ background: '#3b82f6' }}><Calendar size={16} /></div>
            <div>
              <h3>Upcoming Interviews</h3>
              <p>Your confirmed 1-on-1 sessions</p>
            </div>
          </div>
          <button className="dw-cta" onClick={() => navigate('/mock-interview/book')}>
            Book Interview
          </button>
        </div>

        {upcoming.length === 0 ? (
          <div className="dw-empty">
            <Calendar size={28} />
            <p>No upcoming interviews scheduled</p>
          </div>
        ) : (
          <div className="dw-list">
            {upcoming.slice(0, 3).map((r) => {
              // If interviewer rescheduled, show that time; else original requested slot
              const slot = r.rescheduledSlot || r.requestedSlot || r.scheduledAt;
              const link = r.meetingLink;
              return (
                <div key={r.id || r.requestId} className="dw-row">
                  <div className="dw-row-info">
                    <div className="dw-row-name">
                      {r.interviewerName || r.interviewer?.displayName || 'Interviewer'}
                      <span className={`dw-status dw-status-${(r.status || '').toLowerCase()}`}>
                        {r.status || 'Pending'}
                      </span>
                    </div>
                    <div className="dw-row-meta">{fmtDate(slot)} IST</div>
                  </div>
                  <button
                    className="dw-join"
                    disabled={!link}
                    onClick={() => {
                      if (!link) return;
                      const roomId = extractRoomId(link);
                      if (roomId) navigate(`/meeting/${roomId}`);
                      else window.open(link, '_blank');
                    }}
                  >
                    <Video size={13} /> Join
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </section>

      <section className="dw-card">
        <div className="dw-head">
          <div className="dw-head-left">
            <div className="dw-ic" style={{ background: '#a855f7' }}><Bot size={16} /></div>
            <div>
              <h3>AI Interview Feedback</h3>
              <p>Latest practice sessions</p>
            </div>
          </div>
          <button className="dw-link" onClick={() => navigate('/ai-interview/history')}>
            View All <ChevronRight size={14} />
          </button>
        </div>

        {aiSessions.length === 0 ? (
          <div className="dw-empty">
            <Bot size={28} />
            <p>Complete your first AI mock interview</p>
            <button className="dw-cta dw-cta-sm" onClick={() => navigate('/ai-interview')}>
              Start Practice
            </button>
          </div>
        ) : (
          <div className="dw-list">
            {aiSessions.map((s) => {
              const score = s.overallScore ?? s.score ?? s.totalScore;
              const date = s.completedAt || s.endedAt || s.createdAt;
              const summary = s.summary || s.feedback || s.overallFeedback || 'Session completed';
              return (
                <div key={s.id || s.sessionId} className="dw-row" onClick={() => navigate('/ai-interview/history')}>
                  <div className="dw-row-info">
                    <div className="dw-row-name">{fmtDate(date)}</div>
                    <div className="dw-row-meta dw-snip">{String(summary).slice(0, 80)}{String(summary).length > 80 ? '…' : ''}</div>
                  </div>
                  {score != null && (
                    <span className="dw-badge" style={{ background: scoreColor(score), color: '#fff' }}>
                      {Number(score).toFixed(1)}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      <section className="dw-card">
        <div className="dw-head">
          <div className="dw-head-left">
            <div className="dw-ic" style={{ background: '#ec4899' }}><MessageSquare size={16} /></div>
            <div>
              <h3>1-on-1 Feedback</h3>
              <p>Recent interviewer reviews</p>
            </div>
          </div>
          <button className="dw-link" onClick={() => navigate('/mock-interview/my-feedback')}>
            View Details <ChevronRight size={14} />
          </button>
        </div>

        {feedbacks.length === 0 ? (
          <div className="dw-empty">
            <MessageSquare size={28} />
            <p>No feedback yet — book a 1-on-1 interview</p>
            <button className="dw-cta dw-cta-sm" onClick={() => navigate('/mock-interview/book')}>
              Book Now
            </button>
          </div>
        ) : (
          <div className="dw-list">
            {feedbacks.map((f) => {
              const score = f.overallScore ?? f.score;
              const date = f.createdAt || f.feedbackAt || f.submittedAt;
              return (
                <div key={f.id || f.requestId} className="dw-row" onClick={() => navigate('/mock-interview/my-feedback')}>
                  <div className="dw-row-info">
                    <div className="dw-row-name">{f.interviewerName || 'Interviewer'}</div>
                    <div className="dw-row-meta">{fmtDate(date)}</div>
                  </div>
                  {score != null && (
                    <span className="dw-stars">
                      <Star size={13} fill={scoreColor(score)} stroke={scoreColor(score)} />
                      <strong style={{ color: scoreColor(score) }}>{Number(score).toFixed(1)}</strong>
                      <span className="dw-stars-out">/10</span>
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
};

export default DashboardWidgets;
